import { useState } from 'react';
import { router } from '@inertiajs/react';
import { useTranslation } from 'react-i18next';
import TeacherShell, { TeacherPageHeader } from '../../../components/layout/TeacherShell';
import { Card, Button, Input, InputLabel, InputGroup } from '../../../components/ui';
import { cn } from '../../../lib/cn';

const roleBadgeColors = {
  admin: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  teacher: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  student: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
};

export default function ProfileShow({ profile }) {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    first_name: profile.first_name || '',
    last_name: profile.last_name || '',
    email: profile.email || '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    router.patch('/teacher/profile', { profile: formData }, {
      onFinish: () => {
        setIsSubmitting(false);
        setIsEditing(false);
      },
    });
  };

  const handleCancel = () => {
    setFormData({
      first_name: profile.first_name || '',
      last_name: profile.last_name || '',
      email: profile.email || '',
    });
    setIsEditing(false);
  };

  const memberSince = new Date(profile.created_at).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <TeacherShell scrollTitle={t('teacherProfile.title')}>
      <TeacherPageHeader
        title={t('teacherProfile.title')}
        subtitle={t('teacherProfile.subtitle')}
        actions={
          !isEditing && (
            <Button variant="secondary" onClick={() => setIsEditing(true)}>
              {t('common.edit')}
            </Button>
          )
        }
      />

      <div className="max-w-2xl">
        <Card className="p-6">
          {isEditing ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <InputGroup>
                  <InputLabel htmlFor="first_name">{t('auth.firstName')}</InputLabel>
                  <Input
                    id="first_name"
                    value={formData.first_name}
                    onChange={handleChange('first_name')}
                    placeholder="First name"
                  />
                </InputGroup>

                <InputGroup>
                  <InputLabel htmlFor="last_name">{t('auth.lastName')}</InputLabel>
                  <Input
                    id="last_name"
                    value={formData.last_name}
                    onChange={handleChange('last_name')}
                    placeholder="Last name"
                  />
                </InputGroup>
              </div>

              <InputGroup>
                <InputLabel htmlFor="email">{t('common.email')}</InputLabel>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange('email')}
                  placeholder={t('profile.emailPlaceholder')}
                />
              </InputGroup>

              <div className="flex items-center gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <Button type="submit" variant="primary" disabled={isSubmitting}>
                  {isSubmitting ? t('common.saving') : t('common.save')}
                </Button>
                <Button type="button" variant="ghost" onClick={handleCancel}>
                  {t('common.cancel')}
                </Button>
              </div>
            </form>
          ) : (
            <dl className="space-y-4">
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('common.name')}</dt>
                <dd className="mt-1 text-gray-900 dark:text-white">{profile.full_name}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('common.email')}</dt>
                <dd className="mt-1 text-gray-900 dark:text-white">{profile.email}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('common.role')}</dt>
                <dd className="mt-1">
                  <span className={cn('text-xs font-medium px-2 py-0.5 rounded', roleBadgeColors[profile.role] || 'bg-gray-100 text-gray-600')}>
                    {profile.role}
                  </span>
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Member since</dt>
                <dd className="mt-1 text-gray-900 dark:text-white">{memberSince}</dd>
              </div>
            </dl>
          )}
        </Card>
      </div>
    </TeacherShell>
  );
}
